<template>
    <div class="report-problem-page">
        <PageTitleBanner :title="$t('m.reportProblemTitle')" showBack/>

        <main class="report-problem-body">
            <section class="report-card device-card">
                <div class="report-label-row">
                    <label class="report-label" for="report-device-code">
                        {{ $t("m.reportDeviceCode") }}
                        <span class="report-required-mark" aria-hidden="true">*</span>
                    </label>
                </div>
                <div class="device-code-field">
                    <input
                        id="report-device-code"
                        v-model.trim="form.deviceCode"
                        class="device-code-input"
                        type="text"
                        autocomplete="off"
                        :placeholder="$t('m.reportDeviceCodePlaceholder')"
                    >
                    <button
                        v-active-touch
                        class="device-scan-btn interactive-icon"
                        type="button"
                        :aria-label="$t('m.reportScanQrCode')"
                        @click="isScannerOpen = true"
                    >
                        <ScanIcon aria-hidden="true" />
                    </button>
                </div>
            </section>

            <section class="report-card problem-card">
                <div class="report-label-row">
                    <div class="report-label">
                        {{ $t("m.reportProblemType") }}
                        <span class="report-required-mark" aria-hidden="true">*</span>
                    </div>
                </div>
                <div class="problem-options" role="radiogroup" :aria-label="$t('m.reportProblemTypeAria')">
                    <button
                        v-for="problem in problemOptions"
                        :key="problem.value"
                        v-active-touch
                        class="problem-option interactive-surface"
                        :class="{ active: form.type === problem.value }"
                        type="button"
                        role="radio"
                        :aria-checked="form.type === problem.value ? 'true' : 'false'"
                        @click="form.type = problem.value"
                    >
                        {{ problem.label }}
                    </button>
                </div>
            </section>

            <section class="report-card picture-card">
                <div class="report-label-row">
                    <div class="report-label">{{ $t("m.reportTakePicture") }}</div>
                </div>
                <div class="picture-list">
                    <button
                        v-active-touch
                        class="picture-add-btn interactive-surface"
                        type="button"
                        :aria-label="$t('m.reportChooseOrTakePicture')"
                        @click="openImagePicker"
                    >
                        <span aria-hidden="true"></span>
                    </button>
                    <div
                        v-for="image in selectedImages"
                        :key="image.id"
                        class="picture-thumb"
                    >
                        <img :src="image.url" alt="">
                        <button
                            v-active-touch
                            class="picture-remove interactive-icon"
                            type="button"
                            :aria-label="$t('m.reportRemovePicture')"
                            @click="removeImage(image.id)"
                        ></button>
                    </div>
                </div>
                <input
                    ref="imageInput"
                    class="picture-input"
                    type="file"
                    accept="image/*"
                    multiple
                    @change="handleImageChange"
                >
            </section>

            <section class="report-card description-card">
                <div class="report-label-row">
                    <label class="report-label" for="report-description">{{ $t("m.reportDescribeProblem") }}</label>
                </div>
                <textarea
                    id="report-description"
                    v-model="form.desc"
                    class="description-input"
                    maxlength="1000"
                    :placeholder="$t('m.reportDescriptionPlaceholder')"
                    @input="trimDescription"
                ></textarea>
            </section>

        </main>

        <div class="report-submit-bar">
            <button
                v-active-touch
                class="primary-btn report-submit-btn"
                :class="{ invalid: !canSubmit }"
                :disabled="!canSubmit"
                type="button"
                @click="submitReport"
            >
                {{ $t("m.reportSubmit") }}
            </button>
        </div>

        <ScannerOverlay
            v-if="isScannerOpen"
            :scan-parser="parseDeviceCodeFromScanResult"
            @close="isScannerOpen = false"
            @scan="handleScanResult"
        />
    </div>
</template>

<script>
import PageTitleBanner from "@/components/PageTitleBanner.vue";
import ScannerOverlay from "@/components/ScannerOverlay.vue";
import ScanIcon from "@/components/icons/ScanIcon.vue";
import { reportProblem } from "@/api/logined";

const DESCRIPTION_MAX_LENGTH = 1000;

export default {
    name: "ReportProblem",
    components: {
        PageTitleBanner,
        ScannerOverlay,
        ScanIcon,
    },
    data() {
        return {
            form: {
                deviceCode: "",
                type: 1,
                desc: "",
            },
            selectedImages: [],
            isScannerOpen: false,
            isSubmitting: false,
        };
    },
    computed: {
        canSubmit() {
            return !!this.form.deviceCode.trim() && !this.isSubmitting;
        },
        problemOptions() {
            return [
                { label: this.$t("m.reportProblemPowerBankNotEjected"), value: 1 },
                { label: this.$t("m.reportProblemCannotChargeDevice"), value: 2 },
                { label: this.$t("m.reportProblemOrderNotEnded"), value: 3 },
                { label: this.$t("m.reportProblemCableDamagedLost"), value: 4 },
                { label: this.$t("m.reportProblemOther"), value: 5 },
            ];
        },
    },
    beforeDestroy() {
        this.selectedImages.forEach((image) => URL.revokeObjectURL(image.url));
    },
    methods: {
        openImagePicker() {
            if (this.$refs.imageInput) {
                this.$refs.imageInput.click();
            }
        },
        handleImageChange(event) {
            const files = Array.from((event.target && event.target.files) || []);
            const images = files
                .filter((file) => file && file.type && file.type.startsWith("image/"))
                .map((file) => ({
                    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    file,
                    url: URL.createObjectURL(file),
                }));

            this.selectedImages = this.selectedImages.concat(images);
            if (event.target) {
                event.target.value = "";
            }
        },
        removeImage(id) {
            const image = this.selectedImages.find((item) => item.id === id);
            if (image) {
                URL.revokeObjectURL(image.url);
            }
            this.selectedImages = this.selectedImages.filter((item) => item.id !== id);
        },
        trimDescription() {
            if (this.form.desc.length > DESCRIPTION_MAX_LENGTH) {
                this.form.desc = this.form.desc.slice(0, DESCRIPTION_MAX_LENGTH);
            }
        },
        parseDeviceCodeFromScanResult(text) {
            const result = String(text || "").trim();
            if (!result) return "";

            try {
                const url = new URL(result);
                const pathParts = url.pathname.split("/").filter(Boolean);
                return pathParts.length ? pathParts[pathParts.length - 1] : "";
            } catch (_) {
                return "";
            }
        },
        handleScanResult(text) {
            this.form.deviceCode = String(text || "").trim();
            this.isScannerOpen = false;
        },
        buildPayload() {
            const formData = new FormData();
            formData.append("qr_code", this.form.deviceCode.trim());
            formData.append("type", String(this.form.type));

            const desc = this.form.desc.trim();
            if (desc) {
                formData.append("desc", desc);
            }

            this.selectedImages.forEach((image) => {
                formData.append("image", image.file);
            });

            return formData;
        },
        async submitReport() {
            if (!this.canSubmit) return;

            this.isSubmitting = true;
            this.showLoading();
            try {
                const res = await reportProblem(this.buildPayload());
                this.toast((res && res.message) || this.$t("m.reportSubmittedSuccessfully"));
                this.$router.replace({ path: "/help" }).catch(() => {});
            } catch (error) {
                this.toast(error && error.message);
            } finally {
                this.isSubmitting = false;
                this.hideLoading();
            }
        },
    },
};
</script>

<style lang="scss">
.report-problem-page {
    min-height: 100vh;
    min-height: 100dvh;
    background: #f6f6f6;
    overflow-x: hidden;
}

.report-problem-body {
    min-height: calc(100vh - 44px);
    min-height: calc(100dvh - 44px);
    box-sizing: border-box;
    padding: 12px 12px calc(70px + env(safe-area-inset-bottom));
    overflow-x: hidden;
}

.report-card {
    width: 100%;
    margin: 0 0 10px;
    padding: 12px;
    border-radius: 5px;
    background: #fff;
}

.report-label-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.report-label {
    display: block;
    margin: 0;
    color: #333;
    font-size: 15px;
    font-weight: 400;
    line-height: 19px;
}

.report-required-mark {
    color: var(--theme-selectable-accent-color, #276ce1);
    font-size: 14px;
    line-height: 1;
    position: relative;
    top: -1px;
    vertical-align: baseline;
}

.device-code-field {
    position: relative;
}

.device-code-input {
    width: 100%;
    height: 44px;
    padding: 0 54px 0 13px;
    border: 1px solid #e6e6e6;
    border-radius: 5px;
    outline: none;
    color: #333;
    font-family: SF Pro Text, Avenir, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 44px;
}

.device-code-input::placeholder,
.description-input::placeholder {
    color: #c6c6c6;
}

.device-scan-btn {
    position: absolute;
    top: 7px;
    right: 12px;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 50%;
    background: var(--theme-selectable-accent-color, #276ce1);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}

.device-scan-btn svg {
    width: 16px;
    height: 16px;
    color: #fff;
}

.problem-card {
    padding-bottom: 12px;
}

.problem-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px 9px;
}

.problem-option {
    width: 100%;
    min-height: 44px;
    padding: 5px 9px;
    border: 1px solid #e6e6e6;
    border-radius: 5px;
    background: #fff;
    color: #666;
    font-family: SF Pro Text, Avenir, Helvetica, Arial, sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 15px;
    text-align: center;
    overflow-wrap: anywhere;
    display: flex;
    align-items: center;
    justify-content: center;
}

.problem-option.active {
    border-color: var(--theme-selectable-accent-color, #276ce1);
    background: var(--theme-selectable-accent-bg, #fafcff);
    color: var(--theme-selectable-accent-color, #276ce1);
}

.picture-card {
    min-height: 107px;
}

.picture-list {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 5px 5px 0 0;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.picture-list::-webkit-scrollbar {
    display: none;
}

.picture-add-btn,
.picture-thumb {
    flex: 0 0 auto;
    width: 56px;
    height: 56px;
    border: 1px solid #e6e6e6;
    border-radius: 5px;
    background: #fff;
}

.picture-add-btn {
    position: relative;
}

.picture-add-btn span::before,
.picture-add-btn span::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    width: 18px;
    border-top: 2px solid #333;
    transform: translate(-50%, -50%);
}

.picture-add-btn span::after {
    transform: translate(-50%, -50%) rotate(90deg);
}

.picture-thumb {
    position: relative;
    overflow: visible;
}

.picture-thumb img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: inherit;
}

.picture-remove {
    position: absolute;
    top: -5px;
    right: -5px;
    width: 20px;
    height: 20px;
    border: 0;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.52);
}

.picture-remove::before,
.picture-remove::after {
    content: "";
    position: absolute;
    top: 9px;
    left: 5px;
    width: 10px;
    border-top: 1.4px solid #fff;
}

.picture-remove::before {
    transform: rotate(45deg);
}

.picture-remove::after {
    transform: rotate(-45deg);
}

.picture-input {
    display: none;
}

.description-card {
    min-height: 147px;
}

.description-input {
    display: block;
    width: 100%;
    height: 96px;
    padding: 10px 13px;
    border: 1px solid #e6e6e6;
    border-radius: 5px;
    outline: none;
    resize: none;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    color: #333;
    font-family: SF Pro Text, Avenir, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 18px;
}

.report-submit-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 5;
    padding: 0 12px calc(16px + env(safe-area-inset-bottom));
    background: linear-gradient(180deg, rgba(246, 246, 246, 0) 0%, #f6f6f6 24px);
    pointer-events: none;
}

.report-submit-btn {
    width: 100% !important;
    height: 46px !important;
    line-height: 46px !important;
    pointer-events: auto;
}

@media (max-height: 700px) {
    .report-problem-body {
        padding-top: 10px;
        padding-bottom: calc(84px + env(safe-area-inset-bottom));
    }

    .report-card {
        margin-bottom: 8px;
        padding: 10px 12px;
    }

    .report-label-row {
        margin-bottom: 7px;
    }

    .report-label {
        font-size: 14px;
        line-height: 18px;
    }

    .report-required-mark {
        margin-left: 1px;
        font-size: 13px;
        top: -1px;
    }

    .device-code-input {
        height: 40px;
        padding-right: 50px;
        line-height: 40px;
    }

    .device-scan-btn {
        top: 6px;
        width: 28px;
        height: 28px;
    }

    .device-scan-btn svg {
        width: 15px;
        height: 15px;
    }

    .problem-card {
        padding-bottom: 10px;
    }

    .problem-options {
        gap: 8px;
    }

    .problem-option {
        min-height: 38px;
        padding: 4px 8px;
    }

    .picture-card {
        min-height: 85px;
    }

    .picture-list {
        gap: 8px;
        padding-top: 9px;
        padding-right: 9px;
    }

    .picture-add-btn,
    .picture-thumb {
        width: 50px;
        height: 50px;
    }

    .picture-add-btn span::before,
    .picture-add-btn span::after {
        width: 16px;
    }

    .picture-remove {
        width: 18px;
        height: 18px;
        transform: translate(38%, -38%);
    }

    .picture-remove::before,
    .picture-remove::after {
        top: 8px;
        left: 4px;
    }

    .description-card {
        min-height: 113px;
    }

    .description-input {
        height: 78px;
        padding: 9px 12px;
    }

    .report-submit-btn {
        height: 44px !important;
        line-height: 44px !important;
    }

    .report-submit-bar {
        padding-bottom: calc(14px + env(safe-area-inset-bottom));
    }
}
</style>
